// ===========================================
// SERVICE: Conceptos por responsable (CORE) — ORR-230 fase 2
// ===========================================
//
// Reparte los conceptos de un cliente (impuestos y obligaciones recurrentes) entre
// los colaboradores que ya son responsables de ese cliente. Un concepto sin nadie
// asignado queda a cargo de todos: la ausencia de fila NO es un error, es el default.
//
// La tabla `cliente_responsable_conceptos` vive aparte de `cliente_responsables`
// porque agregar la columna ahí rompería su unicidad (cliente_id, usuario_id), que
// hoy sostiene el helper `es_responsable_o_admin` y las 19 policies de scoping.

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { fetchAllPages } from '@/lib/postgrest-chunks'

export type ConceptoResponsableRow =
  Database['public']['Tables']['cliente_responsable_conceptos']['Row']

export type TipoConcepto = 'impuesto' | 'obligacion'

/** Una asignación con los nombres ya resueltos, lista para pintar. */
export interface ConceptoAsignado {
  id: string
  clienteId: string
  usuarioId: string
  usuarioNombre: string
  tipo: TipoConcepto
  /** id del impuesto o de la obligación, según `tipo`. */
  conceptoId: string
  conceptoNombre: string
}

/**
 * Una asignación sin nombres resueltos, para filtrar. La planilla del calendario solo necesita
 * saber quién tiene qué; los nombres son cosa del reparto en el detalle del cliente.
 */
export interface AsignacionConcepto {
  clienteId: string
  usuarioId: string
  tipo: TipoConcepto
  conceptoId: string
}

export interface AsignarConceptoInput {
  organizacionId: string
  clienteId: string
  usuarioId: string
  /** Exactamente uno de los dos. El CHECK de la tabla rechaza ambos o ninguno. */
  impuestoId?: string | null
  obligacionRecurrenteId?: string | null
}

// PostgREST resuelve los nombres en la misma query. Los alias evitan depender del
// nombre de la FK, que cambia si alguna vez se recrea la constraint.
const SELECT_CON_NOMBRES = `
  id,
  cliente_id,
  usuario_id,
  impuesto_id,
  obligacion_recurrente_id,
  usuario:usuarios_brainhub!cliente_responsable_conceptos_usuario_id_fkey (nombre, apellido, email),
  impuesto:catalogo_impuestos!cliente_responsable_conceptos_impuesto_id_fkey (nombre, nombre_corto),
  obligacion:obligaciones_recurrentes!cliente_responsable_conceptos_obligacion_recurrente_id_fkey (nombre)
`

interface FilaCruda {
  id: string
  cliente_id: string
  usuario_id: string
  impuesto_id: string | null
  obligacion_recurrente_id: string | null
  usuario: { nombre: string | null; apellido: string | null; email: string | null } | null
  impuesto: { nombre: string | null; nombre_corto: string | null } | null
  obligacion: { nombre: string | null } | null
}

const nombreUsuario = (u: FilaCruda['usuario']): string => {
  if (!u) return 'Usuario desconocido'
  const completo = [u.nombre, u.apellido].filter(Boolean).join(' ').trim()
  // El email como último recurso: un usuario recién invitado puede no tener nombre.
  return completo || u.email || 'Usuario desconocido'
}

/**
 * Descarta las filas que no se pueden mostrar en vez de romper el listado entero.
 * Pasa si el impuesto o la obligación fue borrado y el CASCADE todavía no corrió,
 * o si RLS deja ver la asignación pero no el concepto.
 */
const aAsignado = (fila: FilaCruda): ConceptoAsignado | null => {
  const esImpuesto = fila.impuesto_id !== null
  const conceptoId = esImpuesto ? fila.impuesto_id : fila.obligacion_recurrente_id
  const nombre = esImpuesto
    ? (fila.impuesto?.nombre_corto ?? fila.impuesto?.nombre)
    : fila.obligacion?.nombre

  if (!conceptoId || !nombre) return null

  return {
    id: fila.id,
    clienteId: fila.cliente_id,
    usuarioId: fila.usuario_id,
    usuarioNombre: nombreUsuario(fila.usuario),
    tipo: esImpuesto ? 'impuesto' : 'obligacion',
    conceptoId,
    conceptoNombre: nombre,
  }
}

/** La fila que ya existía cuando el INSERT choca contra un índice único parcial. */
const buscarAsignacion = async (
  client: SupabaseClient<Database>,
  input: AsignarConceptoInput,
): Promise<ConceptoResponsableRow> => {
  const base = client
    .from('cliente_responsable_conceptos')
    .select('*')
    .eq('cliente_id', input.clienteId)
    .eq('usuario_id', input.usuarioId)

  const { data, error } = await (input.impuestoId
    ? base.eq('impuesto_id', input.impuestoId)
    : base.eq('obligacion_recurrente_id', input.obligacionRecurrenteId!)
  ).single()

  if (error) throw new Error(`Error al recuperar la asignación existente: ${error.message}`)
  return data as ConceptoResponsableRow
}

export const conceptosService = {
  /**
   * Conceptos repartidos de un cliente. Devuelve [] cuando no hay ninguno, que es
   * el estado normal: sin filas, todos los responsables ven todos los conceptos.
   */
  async listarPorCliente(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string,
  ): Promise<ConceptoAsignado[]> {
    const { data, error } = await client
      .from('cliente_responsable_conceptos')
      .select(SELECT_CON_NOMBRES)
      .eq('organizacion_id', organizacionId)
      .eq('cliente_id', clienteId)

    if (error) throw new Error(`Error al listar conceptos asignados: ${error.message}`)

    return ((data ?? []) as unknown as FilaCruda[])
      .map(aAsignado)
      .filter((c): c is ConceptoAsignado => c !== null)
      .sort((a, b) => a.conceptoNombre.localeCompare(b.conceptoNombre, 'es'))
  },

  /**
   * Todas las asignaciones de la organización, sin resolver nombres.
   *
   * La planilla del calendario cruza esto contra cada vencimiento, así que necesita el reparto
   * completo y no el de un cliente: un concepto puede estar repartido en un cliente y libre en
   * otro. Se lee paginado porque no tiene tope propio y PostgREST corta en 1000 filas sin avisar.
   */
  async listarPorOrganizacion(
    client: SupabaseClient<Database>,
    organizacionId: string,
  ): Promise<AsignacionConcepto[]> {
    const filas = await fetchAllPages<ConceptoResponsableRow>(async (desde, hasta) => {
      const { data, error } = await client
        .from('cliente_responsable_conceptos')
        .select('*')
        .eq('organizacion_id', organizacionId)
        .order('id')
        .range(desde, hasta)

      if (error) throw new Error(`Error al listar el reparto de conceptos: ${error.message}`)
      return (data ?? []) as ConceptoResponsableRow[]
    })

    return filas.map((fila) => ({
      clienteId: fila.cliente_id,
      usuarioId: fila.usuario_id,
      tipo: fila.impuesto_id ? 'impuesto' : 'obligacion',
      conceptoId: (fila.impuesto_id ?? fila.obligacion_recurrente_id) as string,
    }))
  },

  /**
   * Asigna un concepto a un responsable.
   *
   * Idempotente: reasignar lo mismo devuelve la fila que ya estaba.
   *
   * Es un INSERT con manejo del 23505 y no un `upsert`, porque los dos índices únicos de la
   * fase 1 son PARCIALES (`WHERE impuesto_id IS NOT NULL`) y Postgres solo infiere un índice
   * parcial si la sentencia repite su predicado — algo que PostgREST no puede mandar, porque
   * `onConflict` acepta columnas y nada más. Sin ese predicado la sentencia muere antes de
   * tocar la tabla, con 42P10 "no unique or exclusion constraint matching the ON CONFLICT
   * specification". Medido contra PROD el 18/08 con EXPLAIN, que planifica sin escribir: sin
   * el WHERE da 42P10, y con el WHERE llega hasta el chequeo de permisos (42501).
   *
   * La unicidad la siguen sosteniendo los mismos dos índices; lo único que cambia es quién
   * detecta el choque: antes se le pedía a Postgres que lo resolviera solo, ahora lo informa
   * y se resuelve acá.
   */
  async asignar(
    client: SupabaseClient<Database>,
    input: AsignarConceptoInput,
  ): Promise<ConceptoResponsableRow> {
    const esImpuesto = Boolean(input.impuestoId)

    if (esImpuesto === Boolean(input.obligacionRecurrenteId)) {
      throw new Error('Indicá exactamente un concepto: un impuesto o una obligación recurrente')
    }

    const { data, error } = await client
      .from('cliente_responsable_conceptos')
      .insert({
        organizacion_id: input.organizacionId,
        cliente_id: input.clienteId,
        usuario_id: input.usuarioId,
        impuesto_id: input.impuestoId ?? null,
        obligacion_recurrente_id: input.obligacionRecurrenteId ?? null,
      })
      .select()
      .single()

    // 23505: el concepto ya estaba asignado a ese responsable. Es el caso idempotente, no un
    // fallo — pasa con doble click o con dos personas repartiendo el mismo cliente a la vez.
    if (error?.code === '23505') return buscarAsignacion(client, input)
    if (error) throw new Error(`Error al asignar el concepto: ${error.message}`)
    return data as ConceptoResponsableRow
  },

  /** Quita una asignación. El concepto vuelve a estar a cargo de todos. */
  async desasignar(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('cliente_responsable_conceptos')
      .delete()
      .eq('id', id)

    if (error) throw new Error(`Error al quitar la asignación: ${error.message}`)
  },

  /**
   * ORR-317 — Clientes que tienen este concepto, para el reparto en masa.
   *
   * Misma fuente que ya usa la RLS de ORR-303 para decidir "quién tiene este concepto"
   * (`tiene_concepto_asignado` compara contra `vencimientos_fiscales.impuesto_id` /
   * `obligacion_recurrente_id`): no se inventa una segunda definición de audiencia.
   * Sirve igual para impuesto y para obligación recurrente.
   */
  async clientesConConcepto(
    client: SupabaseClient<Database>,
    organizacionId: string,
    tipo: TipoConcepto,
    conceptoId: string,
  ): Promise<string[]> {
    const columna = tipo === 'impuesto' ? 'impuesto_id' : 'obligacion_recurrente_id'
    const { data, error } = await client
      .from('vencimientos_fiscales')
      .select('cliente_id')
      .eq('organizacion_id', organizacionId)
      .eq(columna, conceptoId)

    if (error) throw new Error(`Error al buscar los clientes del concepto: ${error.message}`)
    return [...new Set((data ?? []).map((f) => f.cliente_id as string))]
  },

  /**
   * ORR-317 — Snapshot (opción A elegida por Lisandro, D-15): asigna el concepto a los
   * clientes de HOY. Un cliente nuevo con este concepto no queda cubierto solo — repetir
   * la acción es el costo aceptado a cambio de no construir una regla persistente para
   * una feature que él mismo pidió como "extra" y que hoy tiene 2 filas en PROD.
   *
   * Reusa `asignar()` por cliente en vez de un insert masivo: los índices únicos son
   * parciales y `asignar()` ya sabe resolver el 23505 idempotente (ver el comentario ahí).
   * Un insert masivo con `onConflict` no puede repetir el predicado parcial por cliente,
   * así que un solo choque tiraría abajo el lote entero.
   */
  async asignarEnMasa(
    client: SupabaseClient<Database>,
    input: { organizacionId: string; usuarioId: string; tipo: TipoConcepto; conceptoId: string },
  ): Promise<{ clientes: number }> {
    const clienteIds = await this.clientesConConcepto(client, input.organizacionId, input.tipo, input.conceptoId)

    for (const clienteId of clienteIds) {
      await this.asignar(client, {
        organizacionId: input.organizacionId,
        clienteId,
        usuarioId: input.usuarioId,
        impuestoId: input.tipo === 'impuesto' ? input.conceptoId : null,
        obligacionRecurrenteId: input.tipo === 'obligacion' ? input.conceptoId : null,
      })
    }

    return { clientes: clienteIds.length }
  },
}
